"use client";

import MediaImage from "@/components/ui/MediaImage";

import { headerMenuPresentation } from "@/utils/header-menu-presentation";

import { headerLogoForState } from "@/utils/header-logos";
import { headerSurfacesInDark } from "@/utils/header-colours";
import { setMenuReveal } from "@/utils/menu-reveal";

import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { useHeaderDesign } from "../HeaderDesignContext";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import type { CMSData, HeaderAction } from "@/cms/models";
import type { ClassicHeaderSettings, NavigationItem } from "@/cms/classic";
import {
  resolveNavigation,
  type HeaderNavigationSettings,
} from "@/utils/header-navigation";
import {
  customerPortalUrl,
  resolveHeaderActions,
} from "@/utils/header-actions";
import HeaderMenu from "@/components/navigation/HeaderMenu";
import HeaderActions from "@/components/navigation/HeaderActions";
import HeaderBottomBar from "@/components/navigation/HeaderBottomBar";
import MenuActions from "@/components/navigation/MenuActions";
import SearchBar from "@/components/ui/SearchBar";
import { lookSchemeSettings, lookSwitchProps } from "@/utils/look-scheme";
import { withDarkLogo } from "@/utils/look-logo";
import LookSchemeSwitch from "@/components/ui/LookSchemeSwitch";
import { useHeaderState } from "../HeaderStateController";
import { isLookSystem } from "@/utils/look-gate";
import { watchScrollDown } from "@/utils/header-state";

export default function ClassicHeader({
  header,
  cmsData,
  appData,
  pageHeader,
  logoDefinition,
}: {
  header: {
    type?: string;
    classic?: ClassicHeaderSettings;
    contentPlacement?: string;
    actions?: HeaderAction[];
  };
  cmsData: CMSData;
  appData?: Parameters<typeof customerPortalUrl>[1] & {
    logo?: { originalUrl?: string | null };
    inverseLogo?: { originalUrl?: string | null };
  };
  pageHeader?: HeaderNavigationSettings;
  logoDefinition?: import("@/utils/header-logos").HeaderLogoInput["definition"];
}) {
  const headerDesign = useHeaderDesign();
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const headerRef = useRef<HTMLElement>(null);
  const menuToggle = useRef<HTMLButtonElement>(null);
  const menuDialog = useRef<HTMLElement | null>(null);
  const [expanded, setExpanded] = useState<string>();
  // Hovering opens a group; the click that usually follows must not close it again.
  const hoverOpenedAt = useRef(0);
  const [visible, setVisible] = useState(true);
  const [atTop, setAtTop] = useState(true);
  const view = useHeaderState();
  const shownTop = view?.settings.top;
  const managed = !!view;
  const top = view ? !view.frame.scrolled : atTop;
  const hidden = view ? view.frame.hidden : !visible;
  const previousScroll = useRef(0);
  const path = usePathname();
  useEffect(() => {
    setOpen(false);
    setExpanded(undefined);
    setVisible(true);
  }, [path]);
  const settings = { ...cmsData.layout?.header?.classic, ...header.classic };
  // A header that is solid from the top takes its own room instead of lying over the content.
  const solidFromTop = view?.settings.top === "solid";
  const overlay = header.contentPlacement === "overlay" && !solidFromTop;
  const phoneOverlay = !!settings.mobileOverlay && !solidFromTop;
  // Headless UI does not pass `style` on, so the circle the menu grows from is written onto the element.
  const revealMenu = (node: HTMLElement | null) => {
    menuDialog.current = node;
    node?.style.setProperty(
      "--classic-header-width",
      settings.maxWidth || "1310px",
    );
    setMenuReveal(node, menuToggle.current);
  };
  useEffect(() => {
    if (!open) return;
    const measure = () => setMenuReveal(menuDialog.current, menuToggle.current);
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open]);
  useEffect(() => {
    if (managed) return;
    previousScroll.current = window.scrollY;
    setAtTop(window.scrollY <= 1);
    const onScroll = () => {
      const current = Math.max(0, window.scrollY);
      setAtTop(current <= 1);
      if (Math.abs(current - previousScroll.current) < 2 && current > 1) return;
      setVisible(
        settings.hideOnScroll === false ||
          open ||
          current <= 1 ||
          current < previousScroll.current,
      );
      if (current > previousScroll.current) setExpanded(undefined);
      previousScroll.current = current;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [settings.hideOnScroll, open, path, managed]);
  useEffect(() => {
    if (!managed || !expanded) return;
    return watchScrollDown(window, () => setExpanded(undefined));
  }, [managed, expanded]);
  const items: NavigationItem[] =
    settings.navigation ??
    cmsData.pages
      .filter((page) => page.includeInMenu && page.type !== "article")
      .map((page) => ({ id: page.id, label: page.menuTitle, pageId: page.id }));
  const navigation = useMemo(
    () =>
      resolveNavigation({
        site: cmsData,
        pages: cmsData.pages,
        type: header.type ?? "ClassicHeader",
        root: cmsData.layout?.header,
        page: pageHeader,
        colours: logoDefinition?.colours,
        top: shownTop,
      }),
    [cmsData, header.type, pageHeader, logoDefinition, shownTop],
  );
  const { bar: barInDark, panel: panelInDark } = headerSurfacesInDark(
    cmsData.theme,
    {
      type: header.type ?? "ClassicHeader",
      config: cmsData.layout?.header,
      page: pageHeader,
      top: shownTop,
    },
    logoDefinition?.colours,
  );
  const actions = useMemo(
    () =>
      Array.isArray(header.actions)
        ? resolveHeaderActions(header.actions, {
            pages: cmsData.pages,
            portalUrl: customerPortalUrl(cmsData.b2cLink, appData),
            theme: cmsData.theme,
            surfaces: { bar: barInDark, panel: panelInDark },
          })
        : undefined,
    [header.actions, cmsData, appData, barInDark, panelInDark],
  );
  const rows = !!navigation && !!actions?.length && !!navigation.bar.length;
  const toggleSearch = () => setSearching((value) => !value);
  const href = (item: NavigationItem) => {
    const page = cmsData.pages.find((page) => page.id === item.pageId);
    return page
      ? `/${page.slug.split("/").filter(Boolean).join("/")}`
      : item.url || "#";
  };
  /** A group header only opens and closes, so its own page comes first in the group unless a child already goes there. */
  const groupLinks = (item: NavigationItem) => {
    const children = item.children ?? [];
    const own = href(item);
    if (own === "#" || children.some((child) => href(child) === own))
      return children;
    return [
      { ...item, id: `${item.id}-own`, children: undefined },
      ...children,
    ];
  };
  const headerSwitch = lookSwitchProps(cmsData, "header");
  const menuSwitch = lookSwitchProps(cmsData, "menu");
  const darkLogo = lookSchemeSettings(cmsData?.theme)?.logo;
  const logoInput = {
    website: cmsData ?? {},
    definition: logoDefinition,
    media: {
      logo: appData?.logo?.originalUrl ?? undefined,
      inverseLogo: appData?.inverseLogo?.originalUrl ?? undefined,
    },
    config: { ...header, type: "ClassicHeader", classic: settings },
  };
  const logoImage = (src: string, marker?: string) =>
    withDarkLogo(
      <MediaImage
        src={src}
        alt=""
        className="re-classic-logo"
        data-re-logo={marker}
        data-isvisible={marker ? !top : undefined}
        suppressHydrationWarning={managed}
      />,
      darkLogo,
    );
  const stateLogo = (scrolled: boolean) =>
    headerLogoForState(logoInput, { scrolled }).logo.url;
  const logo = (menu = false) => {
    const logoUrl = headerLogoForState(logoInput, {
      scrolled: !top,
      menuOpen: menu,
    }).logo.url;
    const states = managed && !menu;
    const [topLogo, switchedLogo] = states
      ? [stateLogo(false), stateLogo(true)]
      : [];
    const both = !!topLogo && !!switchedLogo && topLogo !== switchedLogo;
    return (
      <Link
        href="/"
        aria-label="Forside"
        onClick={() => menu && setOpen(false)}
        data-re-header-logos={both ? "" : undefined}
      >
        {both ? (
          <>
            {logoImage(topLogo, "top")}
            {logoImage(switchedLogo, "switched")}
          </>
        ) : logoUrl ? (
          logoImage(logoUrl, states ? "" : undefined)
        ) : (
          <span>{cmsData.menuTitle || "Forside"}</span>
        )}
      </Link>
    );
  };
  return (
    <>
      <div
        aria-hidden
        className={`re-classic-header-space ${overlay ? "is-overlay" : ""} ${phoneOverlay ? "mobile-overlay" : ""}${rows ? " re-nav-classic-tall" : ""}`}
      />
      <header
        ref={headerRef}
        data-re-header={managed ? "classic" : undefined}
        className={`re-classic-header ${overlay ? "is-overlay" : ""} ${phoneOverlay ? "mobile-overlay" : ""} ${settings.compact ? "is-compact" : ""} ${!top || settings.solid || solidFromTop ? "is-scrolled" : ""} ${hidden && !open ? "is-hidden" : ""}${rows ? " re-nav-classic-tall" : ""}`}
        onFocusCapture={() => setVisible(true)}
        style={
          {
            "--classic-header-width": settings.maxWidth || "1310px",
            "--classic-logo-height": settings.logoHeight || "90px",
          } as React.CSSProperties
        }
      >
        <div className="re-classic-header-inner">
          {navigation ? (
            <HeaderMenu
              lookSystem={isLookSystem(cmsData?.theme)}
              open={open}
              onOpenChange={setOpen}
              presentation={headerMenuPresentation({
                ...header,
                classic: settings,
              })}
              navigation={navigation}
              actions={actions}
              logo={logo()}
              panelLogo={logo(true)}
              layout={rows ? "rows" : "row"}
              rowClassName={rows ? "re-nav-classic-rows" : undefined}
              barClassName="re-classic-desktop-nav"
              barAlign="between"
              bp="xl"
              appearance="classic"
              anchor={headerRef}
              onSearch={toggleSearch}
              schemeSwitch={
                headerSwitch && <LookSchemeSwitch {...headerSwitch} />
              }
              panelExtras={menuSwitch && <LookSchemeSwitch {...menuSwitch} />}
            />
          ) : (
            <>
              {logo()}
              <nav aria-label="Hovedmenu" className="re-classic-desktop-nav">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="re-classic-nav-group"
                    data-expanded={expanded === item.id}
                    onMouseEnter={() => {
                      if (expanded !== item.id)
                        hoverOpenedAt.current = Date.now();
                      setExpanded(item.id);
                    }}
                    onMouseLeave={() => setExpanded(undefined)}
                    onBlur={(event) => {
                      if (
                        !event.currentTarget.contains(
                          event.relatedTarget as Node,
                        )
                      )
                        setExpanded(undefined);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        setExpanded(undefined);
                        event.currentTarget.querySelector("button")?.focus();
                      }
                    }}
                  >
                    {item.children?.length ? (
                      <>
                        <button
                          type="button"
                          className="re-classic-nav-trigger"
                          aria-controls={`classic-nav-${item.id}`}
                          onClick={() => {
                            if (expanded !== item.id) setExpanded(item.id);
                            else if (Date.now() - hoverOpenedAt.current > 600)
                              setExpanded(undefined);
                          }}
                          aria-expanded={expanded === item.id}
                        >
                          {item.label}
                          <span aria-hidden>⌄</span>
                        </button>
                        <div
                          id={`classic-nav-${item.id}`}
                          className="re-classic-dropdown"
                        >
                          {groupLinks(item).map((child) => (
                            <Link
                              key={child.id}
                              href={href(child)}
                              onClick={() => setExpanded(undefined)}
                              aria-current={
                                path === href(child) ? "page" : undefined
                              }
                            >
                              {child.label}
                            </Link>
                          ))}
                        </div>
                      </>
                    ) : (
                      <Link
                        href={href(item)}
                        aria-current={path === href(item) ? "page" : undefined}
                      >
                        {item.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>
              {actions && (
                <HeaderActions
                  actions={actions}
                  bp="xl"
                  onSearch={toggleSearch}
                />
              )}
              {headerSwitch && <LookSchemeSwitch {...headerSwitch} />}
              <button
                ref={menuToggle}
                type="button"
                className="re-classic-menu-toggle"
                aria-label="Åbn menu"
                aria-expanded={open}
                onClick={() => setOpen(true)}
              >
                <span />
                <span />
                <span />
              </button>
            </>
          )}
        </div>
      </header>
      <Dialog
        open={open && !navigation}
        onClose={setOpen}
        transition
        className="re-classic-menu"
        ref={revealMenu}
        style={headerDesign}
      >
        <DialogPanel className="re-classic-menu-panel">
          <DialogTitle className="sr-only">Hovedmenu</DialogTitle>
          <div className="re-classic-menu-top">
            {logo(true)}
            <button
              type="button"
              onClick={() => setOpen(false)}
              aria-label="Luk menu"
            >
              ×
            </button>
          </div>
          <nav aria-label="Mobilmenu">
            {items.map((item) =>
              item.children?.length ? (
                <Disclosure
                  key={item.id}
                  as="div"
                  className="re-classic-menu-group"
                >
                  <DisclosureButton className="re-classic-menu-row re-classic-menu-trigger">
                    {item.label}
                    <span aria-hidden>⌄</span>
                  </DisclosureButton>
                  <DisclosurePanel className="re-classic-menu-children">
                    {groupLinks(item).map((child) => (
                      <Link
                        key={child.id}
                        href={href(child)}
                        onClick={() => setOpen(false)}
                      >
                        {child.label}
                      </Link>
                    ))}
                  </DisclosurePanel>
                </Disclosure>
              ) : (
                <Link
                  className="re-classic-menu-row"
                  key={item.id}
                  href={href(item)}
                  onClick={() => setOpen(false)}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>
          {actions && (
            <MenuActions
              actions={actions}
              bp="xl"
              onSearch={toggleSearch}
              onNavigate={() => setOpen(false)}
            />
          )}
          {menuSwitch && <LookSchemeSwitch {...menuSwitch} />}
        </DialogPanel>
      </Dialog>
      {actions && (
        <HeaderBottomBar actions={actions} bp="xl" onSearch={toggleSearch} />
      )}
      {actions?.some((action) => action.type === "search") && (
        <div className="re-nav-search-layer">
          <SearchBar
            cmsData={cmsData}
            showSearch={searching}
            toggleSearch={toggleSearch}
          />
        </div>
      )}
    </>
  );
}
